import envConfig from 'brand/env';
import hasIn from 'lodash/hasIn';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import store from '@/store';

import {
  digit8RegExp,
  dateFormatsRegExp,
  numAtEndRegExp,
} from '@/config/regex';

const tempIdPrefix = 'TEMP-ID-';

export const randomHex = (len) => {
  const maxlen = 8;
  const min = 16 ** (Math.min(len, maxlen) - 1);
  const max = (16 ** Math.min(len, maxlen)) - 1;
  const n = Math.floor(Math.random() * (max - min + 1)) + min;
  let r = n.toString(16);

  while (r.length < len) {
    r += randomHex(len - maxlen);
  }
  return r;
};

export const genTempID = (length = 20) => (tempIdPrefix + randomHex(length));

export const findTempID = (id) => {
  if (typeof id === 'string') return id.startsWith(tempIdPrefix);
  if (typeof id === 'number') return false;
  return false;
};

/* eslint prefer-template: "off" */
/* eslint no-bitwise: "off" */
/* eslint no-plusplus: "off" */
export const stringToHslColor = (str, s, l) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = hash % 360;
  return 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
};

export const capitalise = (string) => string[0].toUpperCase() + string.slice(1);

export const findEnv = (config, url) => Object.keys(config)
  .find((v) => config[v].url === url) || 'prod';

export const currentEnv = () => envConfig[findEnv(envConfig, window.top.location.origin)];

export const getWorkspaceId = () => localStorage.getItem('yabbr-organisation');

export const yabbrAuthRedirect = (redirectReason) => {
  console.log('Auth redirect due to: ', redirectReason);

  if (localStorage.getItem('yabbr-prevent-auto-redirect')) {
    console.log(`${currentEnv().yabbrPortal}/#/?referrer=${encodeURIComponent(window.location.href)}&reason=get_tokens&samepage=true`)
    return;
  }

  // eslint-disable-next-line
  window.location.href = `${currentEnv().yabbrPortal}/#/?referrer=${encodeURIComponent(window.location.href)}&reason=get_tokens&samepage=true`;
};

// eslint-disable-next-line
export const getChangeWorkspaceLink = () => `${currentEnv().yabbrPortal}/#/change-workspace?referrer=${encodeURIComponent(window.location.href)}&reason=change_workspace&samepage=true&app=polls`;

// eslint-disable-next-line
export const getYabbrLink = () => `${currentEnv().yabbrPortal}/#/`;

export const insertText = (input, newText) => {
  input.focus();
  let cursorStart = input.selectionStart;
  let cursorEnd = input.selectionEnd;
  const text = input.value;
  const start = text.substring(0, cursorStart);
  const end = text.substring(cursorEnd);
  const insertedText = `${start}${newText}${end}`;
  if (!document.execCommand('insertText', false, newText)) {
    // Firefox doesn't support the above command but it will return false
    // Keeping the above command as it enables undo/redo on other browsers
    /* eslint no-param-reassign: "off" */
    input.value = insertedText;
  }

  /* eslint no-multi-assign: "off" */
  cursorStart = cursorEnd = cursorStart + newText.length;
  return { insertedText, cursorStart, cursorEnd };
};

export const downloadLink = (url, target = '_self') => {
  const link = document.createElement('a');
  link.target = target;
  link.href = url;
  link.click();
};

export const dmsId = (type, poll = store.getState().currentRoute.params.pollId) => `${getWorkspaceId()}_poll-${type}_${poll}`;

export const blankDmsRecord = (type, poll) => ({
  id: dmsId(type, poll),
  meta: {},
  body: {},
});

export const sortOnKey = (a, b, key) => {
  if ((a[key] || a) < (b[key] || b)) return -1;
  if ((a[key] || a) > (b[key] || b)) return 1;
  return 0;
};

export const formatPercentage = (val = 0, places = 1) => val.toFixed(places) + '%';

export const formatBytes = (a, b) => {
  if (0 == a) return "0 Bytes";

  var c = 1024,
      d = b || 2,
      e = ["Bytes","KiB","MiB","GiB","TiB","PiB","EiB","ZiB","YiB"],
      f = Math.floor(Math.log(a) / Math.log(c));

  return parseFloat((a / Math.pow(c, f)).toFixed(d)) + " " + e[f];
};

export const nextLetter = (currentLetter) => String.fromCharCode(currentLetter.charCodeAt(0) + 1);

export const getQuestionDisplayNumber = (id) => {
  const { pollId } = store.getState().currentRoute.params;

  if (!hasIn(store.getState(), ['configPollDetail', 'resources', pollId])) return '';

  const details = store.getState().configPollDetail.resources[pollId];

  if (!('questions' in details) || !details.questions[id]) return '';

  const { number } = details.questions[id];

  if (number in details.abTests) {
    return `Q${number}-${String.fromCharCode(65 + details.abTests[number].indexOf(id))}`;
  }

  if (number in details.subquestions) {
    const subQIndex = details.subquestions[number].findIndex((subQ) => subQ.id === id);
    if (subQIndex > -1) return `Q${number}${String.fromCharCode(97 + subQIndex)}`;
  }

  return `Q${number}`;
};

export const getQuestionArray = (questions) => Object.values(questions)
  .map((question) => ({ ...question, displayNum: getQuestionDisplayNumber(question.id) }))
  .sort((a, b) => {
    if (a.number < b.number) return -1;
    if (a.number > b.number) return 1;
    if (a.displayNum < b.displayNum) return -1;
    if (a.displayNum > b.displayNum) return 1;
    return 0;
  });


export const getNextParentQuestion = (currentQuestion, questionArray) => {
  if (!currentQuestion || !questionArray || !questionArray.length) return null;
  const nextParentQuestion = questionArray.find((question) => question.number > currentQuestion.number);
  return nextParentQuestion;
}

export const copyToClipboard = (text, parent = document.body) => {
  const copyText = document.createElement('textarea');
  copyText.style.position = 'absolute';
  copyText.style.left = '-9999px';
  copyText.value = text;
  parent.appendChild(copyText);
  copyText.select();
  document.execCommand('copy');
  parent.removeChild(copyText);
};

export const stringSimilarity = (str1, str2, substringLength = 2, caseSensitive = false) => {
  if (!caseSensitive) {
    str1 = str1.toLowerCase();
    str2 = str2.toLowerCase();
  }

  if (str1.length < substringLength || str2.length < substringLength) return 0;

  const map = new Map();
  for (let i = 0; i < str1.length - (substringLength - 1); i++) {
    const substr1 = str1.substr(i, substringLength);
    map.set(substr1, map.has(substr1) ? map.get(substr1) + 1 : 1);
  }

  let match = 0;
  for (let j = 0; j < str2.length - (substringLength - 1); j++) {
    const substr2 = str2.substr(j, substringLength);
    const count = map.has(substr2) ? map.get(substr2) : 0;
    if (count) {
      map.set(substr2, count - 1);
      match++;
    }
  }

  return (match * 2) / (str1.length + str2.length - ((substringLength - 1) * 2));
};

// eslint-disable-next-line
export const insertString = (str, strInsert, insertIndex) => `${str.substring(0, insertIndex)}${strInsert}${str.substring(strInsert.length + insertIndex, str.length)}`;

const getAllMomentDates = (allMatchedDates, allDateFormats) => {
  if (!allMatchedDates.length || !allDateFormats.length) return [];

  // Reduce list of all provided dates, checking if it is valid in any of the provided formats and has no unused characters
  return allMatchedDates.reduce((dates, curDate) => {
    const formats = allDateFormats.filter((format) => {
      const momentDate = moment(curDate, format);
      // eslint-disable-next-line
      return momentDate.isValid() && !momentDate._pf.unusedTokens.length && !momentDate._pf.unusedInput.length;
    });

    if (!formats.length) return dates;

    dates.push({ momentDate: moment(curDate, formats[0]), formats });
    return dates;
  }, []);
};

export const generateDuplicateName = (name, existingNames = []) => {
  const validateList = (validName) => (existingNames.includes(validName) ? generateDuplicateName(validName, existingNames) : validName);
  // regex to detect dates in the format YYYYMMDD, DDMMYYYY, DD[-./]MM[-./]YY(YY)
  // add additional regex to match other date formats as needed
  const matchedDates = [
    ...new Set(name.match(digit8RegExp)),
    ...new Set(name.match(dateFormatsRegExp)),
  ];
  // date formats compatible with moment, in most cases update this if you've updated matchedDates

  const allDateFormats = [
    'DDMMYYYY',
    'YYYYMMDD',
    'DD/MM/YY',
    'DD/MM/YYYY',
    'DD-MM-YY',
    'DD-MM-YYYY',
    'DD.MM.YY',
    'DD.MM.YYYY',
    'D/M/YY',
    'D/M/YYYY',
    'D-M-YY',
    'D-M-YYYY',
    'D.M.YY',
    'D.M.YYYY',
  ];

  const allMomentDates = getAllMomentDates(matchedDates, allDateFormats);
  const updateToCurrentDate = allMomentDates.length && !allMomentDates.some((date) => moment().isSame(date.momentDate, 'days'));

  if (updateToCurrentDate) {
    // Find correct format of date to insert and at what position
    const targetFormat = allMomentDates.reduce((acc, date) => {
      // Get date in all potential formats
      const targetStrings = date.formats.map((format) => date.momentDate.format(format));
      const indices = targetStrings.map((string) => name.lastIndexOf(string));
      const index = indices.findIndex((i) => i !== -1);

      // If no potential formats match original name or position is earlier than previously found, return previous value
      if (index === -1) return acc;
      if (indices[index] <= acc.dateSubstringIndex) return acc;

      // Correct format found and exists after all other dates, so return current value
      return {
        format: date.formats[index],
        dateSubstringIndex: indices[index],
      };
    }, { format: null, dateSubstringIndex: -1 });

    if (targetFormat.format) {
      const insertDate = insertString(name, moment().format(targetFormat.format), targetFormat.dateSubstringIndex);
      return numAtEndRegExp.test(insertDate) ? insertDate.replace(numAtEndRegExp, '') : insertDate;
    }
  }

  if (!numAtEndRegExp.test(name)) return validateList(`${name} (1)`);

  const match = name.match(numAtEndRegExp);
  const curNum = parseInt(name.substring(match.index + 1, name.length - 1), 10);

  return validateList(name.replace(numAtEndRegExp, `(${curNum + 1})`));
};

export const mergeObjects = (existing = {}, additional = {}) => {
  const target = cloneDeep(existing);

  Object.entries(additional).forEach(([key, val]) => {
    switch (typeof val) {
      case 'object':
        target[key] = (key in target) ? mergeObjects(target[key], val) : val;
        break;
      case 'number':
        target[key] = (key in target) ? target[key] + val : val;
        break;
      default:
        console.warn(`Unhandled field type for merging ${typeof val}`, existing, additional);
    }
  });
  return target;
};

export const chunk = ({ items, chunkSize = 25, itemMap }) => items.reduce((acc, cur) => {
  const item = (itemMap && typeof itemMap === 'function') ? itemMap(cur) : cur;
  (acc[acc.length - 1].length < chunkSize) ? acc[acc.length - 1].push(item) : acc.push([item]);
  return acc;
}, [[]]);

export const exposeConsoleMethods = (methods) => {
  if (!('yppci' in window)) window.yppci = {};
  Object.assign(window.yppci, methods);
};

export const hasOrganisationFeature = (organisation, featureTest) => {
  if (!organisation || !organisation.features || !featureTest || !featureTest.length) return false;
  // eslint-disable-next-line no-useless-escape
  return organisation.features.some((feature) => new RegExp(`^${featureTest.join('\.')}(\.[a-zA-Z]+)*$`).test(feature));
};